import { NextLink, Operation, fromPromise } from '@apollo/client';
// Lib
import { logOut as googleLogout } from 'lib/google-sdk';
import { logOut as facebookLogout } from 'lib/facebook-sdk';
// Types
import { SignOut } from 'api/auth/types/SignOut';
import { RefreshTokens } from 'api/auth/types/RefreshTokens';
// Helpers
import {
  setTokenToCookies,
  getTokenFromCookies,
  clearTokenFromCookies,
  setGuestTokenToCookies,
  getGuestTokenFromCookies,
  clearGuestTokenFromCookies,
} from 'helpers/cookies';
import { isDevelopmentEnv } from 'helpers/env';

export const AUTH_LOST_EVENT = 'auth:lost';

export type FetchResponse<T> = {
  data: T | null;
  errors?: Error[];
};

export let isRefreshing = false;
export let pendingRequests: (() => void)[] = [];

export const setIsRefreshing = (value: boolean) => {
  isRefreshing = value;
};

export const addPendingRequest = (pendingRequest: () => void) => {
  pendingRequests.push(pendingRequest);
};

export const resolvePendingRequests = () => {
  pendingRequests.forEach((callback) => callback());
  pendingRequests = [];
};

/**
 * Drain queued callbacks (rather than silently dropping them) so any
 * `useQuery` waiting on a queued Promise can resolve and surface its error
 * — instead of staying `loading: true` forever. Drained callbacks run their
 * `forward(operation)` against now-empty cookies, hit the
 * `getHasRefreshableToken()` gate in errorLink, and propagate cleanly.
 */
export const clearPendingRequests = () => {
  const queued = pendingRequests;
  pendingRequests = [];
  queued.forEach((callback) => callback());
};

export const getIfHaveTokenError = (message: string) => {
  const normalizedMessage = message.toLowerCase();

  return [
    'Unauthorized',
    // these 2 error are thown for getStreamBySlug when token expires
    'Not Authorized to access rtmpIngestURL on type Stream',
    'Not Authorized to access rtmpIngestKey on type Stream',
    'You are not authorized to make this call.',
    'token is not defined',
    'Forbidden resource',
    'Forbidden',
  ]
    .map((tokenErrorMessage) => tokenErrorMessage.toLowerCase())
    .some((tokenErrorMessage) => {
      return (
        normalizedMessage === tokenErrorMessage ||
        normalizedMessage.endsWith(`: ${tokenErrorMessage}`) ||
        normalizedMessage.includes(`"${tokenErrorMessage}"`) ||
        normalizedMessage.includes(`'${tokenErrorMessage}'`)
      );
    });
};

export const getIfHaveAccessError = (message: string) => {
  const normalizedMessage = message.toLowerCase();

  return [
    'Refresh token is stale',
    'Invalid token',
    'JWT token has expired or invalid',
    'Your account has been deactivated',
  ]
    .map((accessErrorMessage) => accessErrorMessage.toLowerCase())
    .some((accessErrorMessage) => {
      return (
        normalizedMessage === accessErrorMessage ||
        normalizedMessage.endsWith(`: ${accessErrorMessage}`) ||
        normalizedMessage.includes(`"${accessErrorMessage}"`) ||
        normalizedMessage.includes(`'${accessErrorMessage}'`)
      );
    });
};

export const getIfHaveAuthError = (message: string) => {
  return getIfHaveTokenError(message) || getIfHaveAccessError(message);
};

export const clearAuthData = () => {
  setIsRefreshing(false);
  clearPendingRequests();

  clearTokenFromCookies();
  clearGuestTokenFromCookies();

  /**
   * Trigger "fbSdk.logOut()" everywhere except dev env to avoid the error
   * "The method FB.getLoginStatus can no longer be called from http pages."
   */
  if (!isDevelopmentEnv) {
    facebookLogout().then();
  }

  googleLogout().then();

  /**
   * Instead of redirecting to /?unauthorized=true (which dumps the user out of
   * whatever they were doing), dispatch a global event. AppContext subscribes
   * and opens the existing sign-in modal in place — the user re-authenticates
   * and stays on the same page. See DEV-3138.
   */
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_LOST_EVENT));
  }
};

export const getHasRefreshableToken = (): boolean => {
  const token = getTokenFromCookies();
  const guestToken = getGuestTokenFromCookies();

  return Boolean(token?.refreshToken || guestToken?.refreshToken);
};

const getLocalStorageTokens = () => {
  const localStorageToken = getTokenFromCookies();
  const localStorageGuestToken = getGuestTokenFromCookies();
  const accessToken =
    localStorageToken?.accessToken || localStorageGuestToken?.accessToken;
  const refreshToken =
    localStorageToken?.refreshToken || localStorageGuestToken?.refreshToken;

  return {
    localStorageToken,
    localStorageGuestToken,
    accessToken,
    refreshToken,
  };
};

const handleFetch = async <T = any>(
  query: string
): Promise<FetchResponse<T>> => {
  const { accessToken, refreshToken } = getLocalStorageTokens();

  return fetch(process.env.NEXT_PUBLIC_API_HOST || '', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken || ''}`,
      'x-app-platform': 'web',
      'x-app-version': '0.1.0',
    },
    body: JSON.stringify({
      query,
      variables: { input: { refreshToken } },
    }),
  }).then((res) => res.json());
};

export const refreshTokenMutation = `
  mutation RefreshTokens($input: RefreshTokensInput!) {
    refreshTokens(input: $input) {
      accessToken
      refreshToken
    }
  }
`;

export const refreshUserToken = async () => {
  const {
    localStorageToken,
    localStorageGuestToken,
    accessToken,
    refreshToken,
  } = getLocalStorageTokens();

  // Nothing to refresh — clear cleanly instead of issuing a doomed mutation
  // that the backend will 401 and that would re-enter handleAuthError.
  if (!accessToken && !refreshToken) {
    clearAuthData();
    return;
  }

  try {
    const { data } = await handleFetch<RefreshTokens>(refreshTokenMutation);

    const accessToken = data?.refreshTokens?.accessToken;
    const refreshToken = data?.refreshTokens?.refreshToken;

    if (accessToken && refreshToken) {
      if (localStorageToken) {
        setTokenToCookies({
          ...localStorageToken,
          accessToken,
          refreshToken,
        });
      } else if (localStorageGuestToken) {
        setGuestTokenToCookies({
          ...localStorageGuestToken,
          accessToken,
          refreshToken,
        });
      }

      return;
    }

    // No valid token pair returned — treat as auth failure regardless of
    // whether the response had an `errors` field, so we never leave stale
    // tokens in cookies that would loop through handleAuthError forever.
    clearAuthData();
  } catch (error) {
    clearAuthData();
  }
};

export const signOutMutation = `
  mutation SignOut($input: SignOutInput!) {
    singOut(input: $input)
  }
`;

export const signOut = async () => {
  try {
    await handleFetch<SignOut>(signOutMutation);
  } catch (error) {
    console.log('signOut error', error);
  }
};

export const onAuthError = async (errorMessages: string[]) => {
  const haveTokenError = errorMessages.some(getIfHaveTokenError);
  const haveAccessError = errorMessages.some(getIfHaveAccessError);

  const { refreshToken, accessToken } = getLocalStorageTokens();

  if (!refreshToken?.length && accessToken?.length && haveTokenError) {
    clearAuthData();
  } else if (refreshToken?.length) {
    if (haveAccessError) {
      await signOut();
      clearAuthData();
    }

    if (haveTokenError) {
      await refreshUserToken();
    }
  }
};

export const handleAuthError = ({
  operation,
  forward,
  errorMessages,
}: {
  errorMessages: string[];
  operation: Operation;
  forward: NextLink;
}) => {
  if (!isRefreshing) {
    setIsRefreshing(true);

    return fromPromise(
      onAuthError(errorMessages).catch(() => {
        setIsRefreshing(false);

        return forward(operation);
      })
    ).flatMap(() => {
      // Reset flag BEFORE resolving queued callbacks: if any callback throws,
      // forEach propagates synchronously and would otherwise leave
      // `isRefreshing` stuck at true, blocking all future refresh attempts.
      setIsRefreshing(false);
      resolvePendingRequests();

      return forward(operation);
    });
  } else {
    return fromPromise(
      new Promise((resolve) => {
        addPendingRequest(() => resolve(true));
      })
    ).flatMap(() => {
      return forward(operation);
    });
  }
};
